import { AbstractControl, ValidatorFn } from '@angular/forms';
import dayjs from 'dayjs';
import {
  IBookingCalculationBookingCostingDto,
  IBookingCalculationPaymentDto,
} from 'src/lib/repository/booking/booking-calculation.model';
import { BookingExchangeRate } from 'src/lib/repository/booking/booking.model';
import { BookingCostingType, CurrencyCd } from 'src/lib/repository/enums';
import { Utils } from 'src/lib/utils';

/**
 * 통화코드, 숫자
 */
export type CurrencyCdAndAmt = [CurrencyCd | '', number];

/**
 * 날짜 범위 제한 유효성 검사기
 * @param days 최대 허용 일수
 */
export const dateBetweenValidatorFn: (days: number) => ValidatorFn = (
  days: number,
) => {
  return (control: AbstractControl) => {
    const { fromDate, toDate } = control?.parent?.getRawValue() || {};

    if (!fromDate || !toDate) {
      return null;
    }

    const from = dayjs(fromDate);
    const to = dayjs(toDate);
    const diffDate = to.diff(from, 'days');

    if (diffDate <= days) {
      return null;
    }

    return { dateBetween: days };
  };
};

/**
 * 판매가격 목록에서 항목별 합계 획득
 * @param costingList 판매가격 목록
 * @param type 항목
 * @param columnName 원가/판매가
 * @param baseCurrencyCd 기준 통화 코드
 * @param exchangeRate 환율 오브젝트
 */
export function getCostingTotalAmt(
  costingList: IBookingCalculationBookingCostingDto[],
  type: BookingCostingType,
  columnName: 'costTotalAmt' | 'sellingTotalAmt',
  baseCurrencyCd: CurrencyCd,
  exchangeRate: BookingExchangeRate,
): CurrencyCdAndAmt {
  // 타입 일치 목록
  const filteredList =
    costingList?.filter(
      ({ bookingCostingType }) => bookingCostingType === type,
    ) || [];

  if (!filteredList.length) {
    // 데이터 없으면 종료
    return ['', 0];
  }

  // 첫번째 데이터의 통화 코드
  const firstCurrencyCd = filteredList[0].currencyCd;
  // 합계 금액
  const totalAmt = filteredList
    // 환산 금액 목록으로 변환
    .map((costing) => {
      // 첫번째 통화 코드로 환산
      const exchangedAmt = Utils.getExchangedAmt(
        costing[columnName],
        exchangeRate,
        baseCurrencyCd,
        costing.currencyCd,
        firstCurrencyCd,
      );

      return exchangedAmt;
    })
    // 합산
    .reduce((a, b) => a + b, 0);

  return [firstCurrencyCd, totalAmt];
}

/**
 * 결제 목록에서 선불카드 이용액 합계 획득
 */
export function getPrpayTotalAmt(
  paymentList: IBookingCalculationPaymentDto[],
): number {
  let totalAmt = 0;

  paymentList
    ?.filter(({ paymentType }) => paymentType === 'PRPAY')
    ?.forEach((payment) => {
      totalAmt += payment.amt;
    });

  return totalAmt;
}

/**
 * 결제 목록에서 입금 합계 획득
 */
export function getDepositTotalAmt(
  paymentList: IBookingCalculationPaymentDto[],
  fieldName:
    | 'amt'
    | 'depositBookingManagementFee'
    | 'depositFlightsFee'
    | 'depositRefundPrice',
): number {
  let totalAmt = 0;

  paymentList
    ?.filter(({ paymentType }) => paymentType === 'DEPOSIT')
    ?.forEach((payment) => {
      totalAmt += payment[fieldName] || 0;
    });

  return totalAmt;
}
